// SubServiceSelector.jsx
import React from "react";
import {
  Paper,
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { FileUpload, FileUploadOutlined } from "@mui/icons-material";

const SubServiceSelector = ({
  availableSubServices = [],
  currentServiceLabel,
  isMobile = false,
  activeSubServiceKey,
  selectedSubServiceKey,
  handleSubServiceSelect,
  handleBackToServices,
  handleContinue,
  setCurrentStep,
  loading = false,
  handleEdit,
  propsHandlers = {}, // for all handlers like reset, save, etc.
}) => {
  // Group sub-services by type
  const groupedSubServices = availableSubServices.reduce((acc, sub) => {
    if (sub.key.startsWith("ext")) {
      acc.Exterior = acc.Exterior || [];
      acc.Exterior.push(sub);
    } else if (sub.key.startsWith("int")) {
      acc.Interior = acc.Interior || [];
      acc.Interior.push(sub);
    } else {
      acc.Default = acc.Default || [];
      acc.Default.push(sub);
    }
    return acc;
  }, {});

  // Render single sub-service card
  const renderSubServiceCard = (subService) => {
    const isSaved = propsHandlers.isSelectedSubService
      ? propsHandlers.isSelectedSubService(subService.plan, subService.key)
      : false;
    const isActive =
      activeSubServiceKey === subService.key ||
      selectedSubServiceKey === subService.key;

    return (
      <Card
        key={subService.key}
        elevation={isActive ? 3 : 1}
        sx={{
          border: isActive ? "2px solid #4caf50" : "1px solid #e0e0e0",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            elevation: 2,
            borderColor: isActive ? "#4caf50" : "#bdbdbd",
            width: "100%",
          },
        }}
        onClick={() => {
          handleSubServiceSelect(subService); // first click logic

          setTimeout(() => {
            if (isSaved) {
              console.log(">>>isSaved>>");
              handleSubServiceSelect(subService);
            }
          }, 200); // delay in milliseconds
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  bgcolor: "grey.100",
                  color: "text.primary",
                  width: 48,
                  height: 48,
                }}
              >
                <Typography variant="h6">{subService.icon}</Typography>
              </Avatar>
              <Typography variant="subtitle1" fontWeight="500">
                {subService.label}
              </Typography>
            </Box>

            {isSaved && (
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar sx={{ bgcolor: "success.main", width: 24, height: 24 }}>
                  <CheckCircleIcon sx={{ fontSize: 16, color: "white" }} />
                </Avatar>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    propsHandlers.handleResetSubService?.(
                      subService.plan,
                      subService.key
                    );
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {isActive && (
              <Chip
                label="SAVE"
                color="success"
                clickable
                onClick={(e) => {
                  let edit_id = localStorage.getItem("edit_id");
                  if (edit_id) handleEdit?.();
                  e.stopPropagation();
                  propsHandlers.handleInlineSave?.();
                }}
                disabled={
                  !propsHandlers.planHasGeometry?.(subService.plan) ||
                  !propsHandlers.isActiveSubserviceDirty?.(
                    subService.plan,
                    subService.key
                  )
                }
                sx={{ height: 24, fontSize: "0.75rem" }}
              />
            )}
            {isActive && (
              <Tooltip title="Import KML">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    propsHandlers.addKml?.(subService.plan, subService.key);
                  }}
                  sx={{ position: "relative", padding: 1 }}
                >
                  <FileUploadOutlined />
                  <span
                    style={{
                      position: "absolute",
                      top: 3, // adjust vertical position
                      right: -4, // adjust horizontal position
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    kml
                  </span>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Render a section with header and cards
  const renderGroupSection = (title, items) => (
    <Box
      key={title}
      display="flex"
      flexDirection="column"
      gap={1.5}
      mt={2}
      width="100%"
    >
      <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
        {title} Services
      </Typography>
      {items.map(renderSubServiceCard)}
    </Box>
  );

  return (
    <Paper
      elevation={3}
      sx={{
        width: isMobile ? "100%" : 400,
        p: 1,
        display: "flex",
        flexDirection: "column",
        boxShadow: "none",
        alignItems: "center",
        height: "100%",
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        borderBottom="1px solid #e0e0e0"
        gap={2}
      >
        <Typography variant="h6" fontWeight="600" color="text.primary">
          {currentServiceLabel} Services
        </Typography>
        <Button variant="outlined" size="small" onClick={handleBackToServices}>
          Back
        </Button>
      </Box>

      {/* SubServices */}
      <Box display="flex" flexDirection="column" gap={1.5} width="100%">
        {groupedSubServices.Exterior &&
          renderGroupSection("Exterior", groupedSubServices.Exterior)}

        {groupedSubServices.Interior &&
          renderGroupSection("Interior", groupedSubServices.Interior)}

        {groupedSubServices.Default?.map(renderSubServiceCard)}
      </Box>

      {/* Footer */}
      <Divider />
      <Box
        sx={{
          bgcolor: "grey.50",
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleBackToServices}
          sx={{ py: 1.5, mt: 0, height: 40 }}
        >
          Add More Services
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          sx={{ py: 1.5, mt: 0, height: 40 }}
          loading={loading}
          loadingPosition="end"
        >
          Continue
        </Button>
      </Box>
    </Paper>
  );
};

export default SubServiceSelector;
